import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { getTrackStyleFlags, getTrackType } from '@src/utility/trackTypes';
import React, { useRef } from 'react';
import styled from 'styled-components';

import TimelineContextMenu from './TimelineContextMenu';
import { TimelineElement } from './TimelineElement';

import type { ElementState } from '../../renderer/ElementState';
import type { Story } from '../../types.ts/story';
import type { TrackType } from '@src/utility/trackTypes';

// Re-export TrackType for consumers that import from here
export type { TrackType } from '@src/utility/trackTypes';

interface TimelineTrackProps {
  elements: ElementState[];
  story?: Story;
  track?: number;
  isActive: boolean;
  /** Track type for classification and styling. If not provided, derived from elements. */
  trackType?: TrackType;
  onContextMenu?: (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    el: ElementState,
  ) => void;
  handlePasteContextMenu?: (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    track: number,
    elements: ElementState[],
  ) => void;
  width?: number;
  onElementCrtlClick: (element: ElementState) => void;
}

export const TimelineTrack: React.FC<TimelineTrackProps> = props => {
  const ref = useRef<HTMLDivElement>(null);
  const videoCreator = useVideoCreatorStore();
  const firstElement = props.elements[0];
  const firstSource = firstElement?.source;

  // Determine track type: use prop if provided, otherwise derive from elements
  const trackType: TrackType =
    props.trackType ?? getTrackType(props.elements, videoCreator);

  // Derive styling flags from track type
  const styleFlags = getTrackStyleFlags(trackType);

  // Legacy compatibility: linkedVideo/linkedAudio for styling
  const linkedVideo =
    !!firstSource &&
    firstSource.type === 'video' &&
    trackType !== 'linked-audio';
  const linkedAudio = trackType === 'linked-audio';

  return (
    <Main
      ref={ref}
      linkedVideo={linkedVideo}
      isOriginalVideo={styleFlags.isOriginalVideo}
      linkedAudio={linkedAudio}
      isScenesTrack={styleFlags.isScenesTrack}
      isText={styleFlags.isText || styleFlags.isKaraoke}
      isShape={styleFlags.isShape}
      isComposition={styleFlags.isComposition}
      isImage={styleFlags.isImage}
      isActive={props.isActive}
      onContextMenu={e => {
        if (props.handlePasteContextMenu && props.track) {
          props.handlePasteContextMenu(e, props.track, props.elements);
        }
      }}
      isLogo={styleFlags.isLogo}
      style={{ width: props.width }}
      className={'timeline-track-drag-target'}
      data-track-number={props.track}
      data-track-type={trackType}
    >
      <TimelineContextMenu action="paste" />

      {props.elements.map((element, index) => (
        <TimelineElement
          key={`element-${index}-${element.source.id}`}
          trackType={trackType}
          element={element}
          allElements={props.elements}
          story={props.story}
          onContextMenu={props.onContextMenu}
          onElementCrtlClick={props.onElementCrtlClick}
        />
      ))}
    </Main>
  );
};

const Main = styled.div<{
  linkedVideo: boolean;
  linkedAudio: boolean;
  isScenesTrack: boolean;
  isText: boolean;
  isActive: boolean;
  isOriginalVideo: boolean;
  isLogo?: boolean;
  isShape: boolean;
  isComposition?: boolean;
  isImage?: boolean;
}>`
  position: relative;
  height: ${props => {
    if (props.isOriginalVideo) return '59px';
    if (props.linkedAudio) return '40px';
    if (props.isScenesTrack) return '30px';
    if (
      props.isText ||
      props.isLogo ||
      props.isShape ||
      props.isComposition ||
      props.isImage
    )
      return '38px';
    return '78px';
  }};
  padding-top: ${props => (props.linkedAudio ? '0px' : '4px')};
  background-color: ${props =>
    props.isActive
      ? 'color-mix(in srgb, var(--border) 50%, transparent)'
      : 'transparent'};
  box-sizing: border-box;
`;
